import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useCallback, useEffect, useRef, useState } from 'react';
import { HIGHLIGHT_COLORS, HIGHLIGHT_COLOR_LABELS, } from '@leits/shared/client';
import { blockOf, resolveSelectionToParts } from '../lib/annotate.js';
/**
 * 可标注区域：把「选中文字 → 弹工具栏 → 高亮/写笔记 → 点已有标记改颜色或改笔记」
 * 这套交互封装在一个容器上。文章（passage）与精读页的题目区共用它，
 * 所以两处的操作手感与锚点规则完全一致。
 *
 * 注意：容器内容必须由调用方用 innerHTML 写入，不能让 React 托管——
 * 渲染标注时会包裹/切分 DOM 节点，React 一重渲染就会把这些改动抹掉。
 */
export function AnnotationRegion({ anchorKind, containerRef, className, annotations, enabled = true, onCreate, onUpdate, onDelete, }) {
    const [toolbar, setToolbar] = useState(null);
    const [noteMode, setNoteMode] = useState(false);
    const [noteDraft, setNoteDraft] = useState('');
    const [popover, setPopover] = useState(null);
    const [popoverNote, setPopoverNote] = useState('');
    const [hint, setHint] = useState(null);
    const hintTimer = useRef(null);
    const showHint = useCallback((message) => {
        setHint(message);
        if (hintTimer.current)
            window.clearTimeout(hintTimer.current);
        hintTimer.current = window.setTimeout(() => setHint(null), 2600);
    }, []);
    const closeAll = useCallback(() => {
        setToolbar(null);
        setPopover(null);
        setNoteMode(false);
        setNoteDraft('');
    }, []);
    const handleMouseUp = useCallback(() => {
        if (!enabled)
            return;
        const container = containerRef.current;
        if (!container)
            return;
        const selection = window.getSelection();
        if (!selection || selection.isCollapsed || selection.rangeCount === 0) {
            setToolbar(null);
            return;
        }
        const range = selection.getRangeAt(0);
        if (!container.contains(range.startContainer) || !container.contains(range.endContainer)) {
            setToolbar(null);
            return;
        }
        const startBlock = blockOf(range.startContainer);
        const endBlock = blockOf(range.endContainer);
        if (!startBlock || startBlock !== endBlock) {
            setToolbar(null);
            showHint('一次只标注同一个句子块里的文字');
            return;
        }
        const parts = resolveSelectionToParts(startBlock, range);
        if (parts.length === 0) {
            setToolbar(null);
            return;
        }
        const rect = range.getBoundingClientRect();
        setPopover(null);
        setNoteMode(false);
        setNoteDraft('');
        setToolbar({
            x: rect.left + rect.width / 2,
            y: rect.top,
            anchorKind: startBlock.dataset.anchorKind ?? anchorKind,
            anchorId: startBlock.dataset.blockId ?? '',
            parts,
        });
    }, [anchorKind, containerRef, enabled, showHint]);
    const handleClick = useCallback((event) => {
        const target = event.target.closest('mark.anno');
        const id = target?.dataset.annotationId;
        if (!id) {
            setPopover(null);
            return;
        }
        const annotation = annotations.find((item) => item.id === id);
        if (!annotation)
            return;
        const rect = target.getBoundingClientRect();
        setToolbar(null);
        setPopoverNote(annotation.note);
        setPopover({ x: rect.left + rect.width / 2, y: rect.top, annotation });
        event.stopPropagation();
    }, [annotations]);
    useEffect(() => {
        function onKeyDown(event) {
            if (event.key !== 'Escape')
                return;
            window.getSelection()?.removeAllRanges();
            closeAll();
        }
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, [closeAll]);
    const emit = useCallback((color, note) => {
        if (!toolbar)
            return;
        onCreate(toolbar.parts.map((part) => ({
            anchorKind: toolbar.anchorKind,
            anchorId: toolbar.anchorId,
            sentenceIndex: part.sentenceIndex,
            startOffset: part.startOffset,
            endOffset: part.endOffset,
            selectedText: part.text,
            color,
            note,
        })));
        window.getSelection()?.removeAllRanges();
        closeAll();
    }, [closeAll, onCreate, toolbar]);
    return (_jsxs(_Fragment, { children: [_jsx("div", { ref: containerRef, className: className, onMouseUp: handleMouseUp, onClick: handleClick }), hint ? _jsx("div", { className: "toast passage-hint", children: hint }) : null, toolbar ? (_jsx("div", { className: "selection-toolbar", style: { left: toolbar.x, top: toolbar.y }, onMouseDown: (event) => event.preventDefault(), children: noteMode ? (_jsxs("div", { className: "selection-toolbar__note", children: [_jsx("label", { className: "sr-only", htmlFor: "selection-note", children: "\u7B14\u8BB0\u5185\u5BB9" }), _jsx("textarea", { id: "selection-note", className: "input", rows: 3, autoFocus: true, placeholder: "\u5199\u70B9\u4EC0\u4E48\uFF0C\u4F8B\u5982\uFF1A\u8FD9\u53E5\u662F\u5B9A\u4F4D\u53E5 / \u540C\u4E49\u66FF\u6362\u6CA1\u770B\u51FA\u6765", value: noteDraft, onChange: (event) => setNoteDraft(event.target.value) }), _jsxs("div", { className: "row-inline", style: { justifyContent: 'flex-end' }, children: [_jsx("button", { type: "button", className: "btn btn--sm", onClick: closeAll, children: "\u53D6\u6D88" }), _jsx("button", { type: "button", className: "btn btn--primary btn--sm", disabled: noteDraft.trim().length === 0, onClick: () => emit(null, noteDraft.trim()), children: "\u4FDD\u5B58\u7B14\u8BB0" })] })] })) : (_jsxs(_Fragment, { children: [HIGHLIGHT_COLORS.map((color) => (_jsx("button", { type: "button", className: "swatch", "data-color": color, title: `${HIGHLIGHT_COLOR_LABELS[color]}色高亮`, "aria-label": `${HIGHLIGHT_COLOR_LABELS[color]}色高亮`, onClick: () => emit(color, '') }, color))), _jsx("button", { type: "button", className: "btn btn--sm", onClick: () => setNoteMode(true), children: "\u5199\u7B14\u8BB0" }), _jsx("button", { type: "button", className: "btn btn--ghost btn--sm", onClick: closeAll, children: "\u53D6\u6D88" })] })) })) : null, popover ? (_jsxs("div", { className: "anno-popover", style: { left: popover.x, top: popover.y }, role: "dialog", "aria-label": "\u6807\u6CE8\u8BBE\u7F6E", children: [_jsxs("div", { className: "row-inline", style: { justifyContent: 'space-between' }, children: [_jsx("span", { className: "faint", style: { fontSize: 12 }, children: popover.annotation.selectedText.slice(0, 40) || '整句' }), _jsx("button", { type: "button", className: "btn btn--ghost btn--sm", onClick: () => setPopover(null), children: "\u5173\u95ED" })] }), _jsxs("div", { className: "row-inline", style: { margin: '8px 0' }, children: [_jsx("span", { className: "faint", style: { fontSize: 12 }, children: "\u989C\u8272" }), HIGHLIGHT_COLORS.map((color) => (_jsx("button", { type: "button", className: popover.annotation.color === color ? 'swatch is-active' : 'swatch', "data-color": color, "aria-label": `改为${HIGHLIGHT_COLOR_LABELS[color]}色`, onClick: () => {
                                    onUpdate(popover.annotation.id, { color });
                                    setPopover(null);
                                } }, color))), _jsx("button", { type: "button", className: "btn btn--ghost btn--sm", onClick: () => {
                                    onUpdate(popover.annotation.id, { color: null });
                                    setPopover(null);
                                }, children: "\u53BB\u8272" })] }), _jsx("label", { className: "field__label", htmlFor: "popover-note", children: "\u7B14\u8BB0" }), _jsx("textarea", { id: "popover-note", className: "input", rows: 3, value: popoverNote, onChange: (event) => setPopoverNote(event.target.value) }), _jsxs("div", { className: "dialog__actions", children: [_jsx("button", { type: "button", className: "btn btn--sm", onClick: () => {
                                    onDelete(popover.annotation.id);
                                    setPopover(null);
                                }, children: "\u5220\u9664" }), _jsx("button", { type: "button", className: "btn btn--primary btn--sm", onClick: () => {
                                    onUpdate(popover.annotation.id, {
                                        note: popoverNote,
                                        color: popoverNote.trim().length === 0 ? null : popover.annotation.color,
                                    });
                                    setPopover(null);
                                }, children: "\u4FDD\u5B58" })] })] })) : null] }));
}
//# sourceMappingURL=AnnotationRegion.js.map